import { shopError } from "./shop-error.js";
import { shopSections } from "./shop-sections.js";
/** What shoppers read in the storefront (apps/storefront, docs/specs/storefront.md). */
export const shop = {
    language: "Language",
    languages: { en: "English", bn: "বাংলা" },
    comingSoon: {
        title: "Our online shop opens soon",
        body: "We are putting our products online. Call or message us to order today.",
    },
    contact: {
        call: "Call {{phone}}",
        email: "E-mail {{email}}",
    },
    cod: "Cash on delivery available",
    notFound: {
        title: "This shop is not available",
        body: "Check the address, or the shop may be closed for now.",
        code: "404",
        pageTitle: "This page is not here",
        pageBody: "The link may be old or mistyped. Search the shop, or start from a category.",
        search: "Search the shop",
        popular: "Popular categories",
        home: "Back to the home page",
    },
    error: shopError,
    poweredBy: "Online store by Sobthik",
    demoBanner: "This is a demo shop for the theme — have a look around. Ordering is off.",
    nav: {
        home: "Home",
        allProducts: "All products",
        categories: "Categories",
        menu: "Shop menu",
        searchLabel: "Search products",
        searchPlaceholder: "Search products…",
        search: "Search",
    },
    home: {
        heroTitle: "Welcome to {{name}}",
        heroBody: "Order online and get it delivered to your door. Cash on delivery available.",
        heroBodyNoCod: "Order online and get it delivered to your door.",
        shopNow: "Shop now",
        categories: "Shop by category",
        newArrivals: "New arrivals",
        viewAll: "View all",
        empty: "New products are coming soon.",
    },
    price: {
        from: "From {{price}}",
        save: "Save {{percent}}%",
        mrp: "Regular price {{price}}",
    },
    availability: {
        in_stock: "In stock",
        low: "Few left",
        out: "Out of stock",
        onlyLeft: "Only {{quantity}} left",
    },
    list: {
        title: "All products",
        count_one: "{{n}} product",
        count_other: "{{n}} products",
        category: "Category",
        allCategories: "All categories",
        sort: "Sort by",
        sorts: {
            newest: "Newest",
            price_asc: "Price: low to high",
            price_desc: "Price: high to low",
            name: "Name",
        },
        inStock: "In stock only",
        apply: "Apply",
        more: "Show more",
        empty: "No products match. Try another category or remove a filter.",
        filters: "Filters",
    },
    search: {
        title: "Search",
        resultsFor: "Results for “{{q}}”",
        empty: "Nothing found for “{{q}}”. Try a shorter or different word.",
        prompt: "Type what you are looking for.",
    },
    product: {
        breadcrumb: "Breadcrumb",
        choose: "{{option}}",
        unavailable: "This combination is not available.",
        description: "Description",
        details: "Details",
        brand: "Brand",
        category: "Category",
        orderByPhone: "Call to order",
        checkoutSoon: "Online checkout opens soon. Call us to order now.",
        gallery: "Product images",
        image: "Image {{n}} of {{total}}",
        noImage: "No image",
        digital: {
            readOnline: "Read on this website right after paying. Nothing to deliver.",
            buy: "Buy eBook",
            sample: "Read a little",
            tag: "eBook",
            // The "Read a little" window on the product page.
            peekLoading: "Opening the sample…",
            peekFailed: "The sample could not be opened. Please try again.",
            peekEnd: "That is the end of the sample. Buy the eBook to read the whole book.",
        },
        // DG-08 (BK-01): the book's details on its page.
        book: {
            by: "by",
            details: "Book details",
            roles: { author: "Author", translator: "Translator", editor: "Editor" },
            publisher: "Publisher",
            isbn: "ISBN",
            pages: "Pages",
            edition: "Edition",
            language: "Language",
            year: "Published",
            noDownload: "eBook: read on this website after buying. There is no file to download.",
        },
    },
    cart: {
        title: "Your cart",
        add: "Add to cart",
        added: "Added to your cart",
        view: "View cart",
        empty: "Your cart is empty.",
        continue: "Continue shopping",
        quantity: "Quantity",
        update: "Update",
        increase: "One more",
        decrease: "One less",
        addFailed: "Could not add it. Please try again.",
        remove: "Remove",
        subtotal: "Subtotal",
        checkout: "Checkout",
        deliveryNote: "Delivery charge is added at checkout.",
        items_one: "{{count}} item",
        items_other: "{{count}} items",
        problems: {
            unavailable: "No longer available",
            out_of_stock: "Out of stock — remove it to continue",
            not_enough: "Not enough in stock — lower the quantity",
        },
        link: "Cart ({{count}})",
    },
    checkout: {
        title: "Checkout",
        contact: "Your details",
        name: "Full name",
        phone: "Mobile number",
        phoneHint: "We text a code to check the number.",
        address: "Delivery address",
        district: "District",
        chooseDistrict: "Choose a district",
        upazila: "Area / thana",
        chooseUpazila: "Choose an area (optional)",
        addressLine: "House, road, village",
        note: "Note for the shop (optional)",
        payment: "Payment",
        cod: "Cash on delivery",
        codHint: "Pay the delivery person when the parcel arrives.",
        summary: "Order summary",
        delivery: "Delivery",
        deliveryPending: "Choose a district",
        total: "Total",
        sendCode: "Send code",
        resend: "Send again",
        code: "6-digit code",
        verify: "Verify",
        verified: "Number verified",
        codeSent: "We sent a code to {{phone}}.",
        place: "Place order",
        placing: "Placing your order…",
        errorGeneric: "Something went wrong. Please try again.",
        botCheck: "Please complete the check below.",
        notReady: "Fix the items in your cart first.",
        digitalNote: "Digital products are not shipped. They appear in your library as soon as you pay.",
        digitalPayOnline: "Digital products are paid online.",
        digitalNoPayment: "This shop does not take online payments yet, so digital products cannot be bought here now.",
    },
    placed: {
        title: "Thank you! Your order is placed.",
        number: "Order number",
        total: "Total to pay",
        next: "We will call you to confirm the order before sending it.",
        continue: "Continue shopping",
    },
    pay: {
        choose: "How do you want to pay?",
        gateways: { bkash: "bKash", nagad: "Nagad", sslcommerz: "Card or mobile wallet" },
        gatewayHint: "You will pay on the secure page of the payment provider.",
        checking: "Checking your payment…",
        paid: "Payment received. Thank you!",
        failed: "The payment did not go through. Your order was cancelled; you can order again.",
        expired: "The time to pay has passed. Your order was cancelled; you can order again.",
        open: "We have not received the payment yet.",
        retry: "Pay now",
        orderNumber: "Order {{number}}",
        inLibrary: "Your digital products are in your library — open them on this website.",
        openLibrary: "Open my library",
    },
    dashboard: {
        hello: "Hello, {{name}}",
        helloNoName: "Hello",
        nav: {
            overview: "Overview",
            orders: "Orders",
            wishlist: "Saved",
            reviews: "Reviews",
            addresses: "Addresses",
            profile: "Profile",
            security: "Security",
            library: "Library",
            signOut: "Sign out",
        },
        latestOrder: "Latest order",
        noOrders: "You have not ordered online yet.",
        startShopping: "Start shopping",
        allOrders: "All orders",
        ordersCount_one: "{{count}} order",
        ordersCount_other: "{{count}} orders",
        savedCount_one: "{{count}} saved product",
        savedCount_other: "{{count}} saved products",
        tabs: { all: "All", active: "In progress", delivered: "Delivered", cancelled: "Cancelled" },
        searchOrders: "Order number",
        search: "Search",
        noMatch: "No orders here.",
        viewOrder: "View",
        track: "Track parcel",
        courier: "Courier",
        consignment: "Consignment",
        courierStatus: "Courier status",
        invoice: "Invoice (PDF)",
        orderAgain: "Order again",
        cancel: "Cancel order",
        cancelConfirm: "Cancel this order? This cannot be undone.",
        cancelReason: "Reason (optional)",
        cancelled: "The order is cancelled.",
        cannotCancel: "Need to change something? Call the shop.",
        profileTitle: "Profile",
        name: "Name",
        language: "Language for messages",
        offers: "Send me offers by SMS and e-mail",
        save: "Save",
        saved: "Saved",
        email: "Email",
        mobile: "Mobile number",
        notSet: "Not added",
        verified: "Verified",
        notVerified: "Not verified",
        add: "Add",
        change: "Change",
        verify: "Verify",
        newEmail: "New email",
        newMobile: "New mobile number",
        currentPassword: "Current password",
        cancelEdit: "Cancel",
        securityTitle: "Security",
        passwordTitle: "Password",
        setPassword: "Set a password",
        changePassword: "Change password",
        newPassword: "New password",
        passwordChanged: "Password changed. Other devices are signed out.",
        googleTitle: "Google",
        googleLinked: "Linked to Google",
        googleNotLinked: "Not linked",
        unlink: "Unlink",
        devicesTitle: "Signed-in devices",
        thisDevice: "This device",
        unknownDevice: "Unknown device",
        lastActive: "Active {{date}}",
        via_password: "Password",
        via_phone_code: "Mobile code",
        via_google: "Google",
        signOutDevice: "Sign out",
        signOutOthers: "Sign out all other devices",
        deleteTitle: "Delete account",
        deleteBody: "Your name, email, mobile number, addresses and saved products are erased. Your orders stay with the shop for its records.",
        deleteConfirm: "I understand, delete my account",
        deleteButton: "Delete account",
        addressesTitle: "Addresses",
        addressesEmpty: "No saved addresses yet.",
        addAddress: "Add address",
        editAddress: "Edit",
        deleteAddress: "Remove",
        default: "Default",
        makeDefault: "Use as default",
        label: "Label (Home, Office…)",
        recipient: "Recipient's name",
        district: "District",
        upazila: "Area / upazila",
        line: "House, road, area",
        reviewsTitle: "My reviews",
        reviewsEmpty: "You have not written any reviews yet.",
        status_pending: "Waiting for approval",
        status_published: "Published",
        status_hidden: "Hidden",
        shopReply: "Shop's reply",
        toReview: "Waiting for your review",
    },
    // DG-08 (BK-01): an author's page and the popular authors shelf.
    authors: {
        books_one: "{{n}} book",
        books_other: "{{n}} books",
        booksBy: "Books by {{name}}",
        all: "All authors",
    },
    library: {
        title: "My library",
        subtitle: "Books you bought from this shop. Read them here on any device.",
        empty: "Nothing here yet. eBooks you buy appear here right after paying.",
        read: "Read",
        continue: "Continue",
        progress: "Page {{page}} of {{total}}",
        notStarted: "Not started",
        added: "Added {{date}}",
    },
    reader: {
        back: "Back to library",
        previous: "Previous page",
        next: "Next page",
        page: "Page",
        of: "of {{total}}",
        zoomIn: "Zoom in",
        zoomOut: "Zoom out",
        fitWidth: "Fit width",
        fitPage: "Fit page",
        twoPages: "Two pages",
        look: "Page colour",
        light: "Light",
        sepia: "Sepia",
        dark: "Dark",
        fullscreen: "Full screen",
        contents: "Contents",
        noContents: "This book has no table of contents.",
        close: "Close",
        loading: "Opening the book…",
        failed: "The book could not be opened. Please try again.",
        retry: "Try again",
        sample: "Sample",
        sampleNote: "You are reading a sample. Buy the eBook to read all of it.",
        buy: "Buy the eBook",
    },
    auth: {
        signInTitle: "Sign in or create an account",
        signInBody: "Use your email address or mobile number.",
        identifier: "Email or mobile number",
        continue: "Continue",
        change: "Change",
        password: "Password",
        showPassword: "Show password",
        signIn: "Sign in",
        forgot: "Forgot password?",
        useCode: "Sign in with a code instead",
        usePassword: "Use my password",
        signUpTitle: "Create your account",
        signUpBody: "No account uses {{who}} yet. It takes a moment.",
        name: "Your name",
        newPassword: "Choose a password",
        passwordHint: "At least 8 characters. Not your number, not digits only, not a common password.",
        createAccount: "Create account",
        codeSentPhone: "We texted a code to {{to}}.",
        codeSentEmail: "We e-mailed a code to {{to}}.",
        codeNeeded: "This shop asks you to confirm {{who}} with a code first.",
        sendCode: "Send code",
        resend: "Send again",
        code: "6-digit code",
        confirmCode: "Confirm",
        codeConfirmed: "Confirmed",
        signUpOff: "This shop does not take new accounts with this. Try your email or mobile number instead.",
        haveAccount: "Already have an account? Sign in",
        forgotTitle: "Reset your password",
        forgotBody: "Enter the email address or mobile number of your account.",
        sendReset: "Send",
        emailSent: "If an account uses {{to}}, we have e-mailed it a link. The link works for 30 minutes.",
        phoneSent: "If an account uses {{to}}, we have texted it a code.",
        resetTitle: "Choose a new password",
        savePassword: "Save password",
        resetDone: "Your password is changed and you are signed out everywhere. Sign in with the new one.",
        resetLinkMissing: "This link is not complete. Ask for a new one.",
        backToSignIn: "Back to sign in",
        errorGeneric: "Something went wrong. Please try again.",
        verifyTitle: "Verify your mobile number",
        verifyBody: "Confirm {{phone}} to see the orders you placed with it before you had an account.",
        verified: "Your mobile number is verified.",
        or: "or",
    },
    account: {
        title: "My orders",
        link: "My orders",
        signInTitle: "See your orders",
        signInBody: "Enter the mobile number you ordered with. We text you a code.",
        signedInAs: "Signed in as {{who}}",
        signOut: "Sign out",
        empty: "No online orders yet.",
        placedOn: "Placed {{date}}",
        items_one: "{{count}} item",
        items_other: "{{count}} items",
        back: "All my orders",
        status: {
            draft: "Draft",
            pending: "Received",
            confirmed: "Confirmed",
            packed: "Packed",
            shipped: "On the way",
            delivered: "Delivered",
            partially_delivered: "Partly delivered",
            returned: "Returned",
            cancelled: "Cancelled",
        },
        payment: {
            unpaid: "Not paid yet",
            partially_paid: "Partly paid",
            paid: "Paid",
            cod_pending: "Pay on delivery",
            refunded: "Refunded",
        },
        deliverTo: "Delivery address",
        progress: "Progress",
        trackTitle: "Order {{number}}",
    },
    coupon: {
        label: "Coupon code",
        apply: "Apply",
        remove: "Remove",
        discount: "Discount ({{code}})",
        freeDelivery: "Free delivery ({{code}})",
        automatic: "Offer applied: {{code}}",
        problems: {
            COUPON_NOT_APPLICABLE: "This code does not apply to your cart.",
            COUPON_EXPIRED: "This code has expired.",
            COUPON_MIN_ORDER: "Add more to your cart to use this code.",
            COUPON_LIMIT_REACHED: "This code has been used up.",
        },
    },
    reviews: {
        title: "Reviews",
        none: "No reviews yet.",
        average: "{{average}} out of 5",
        count_one: "{{count}} review",
        count_other: "{{count}} reviews",
        reply: "Reply from the shop",
        stars: "{{rating}} stars",
        more: "More reviews",
        write: "Review what you bought",
        rating: "Your rating",
        text: "Your review (optional)",
        send: "Send review",
        thanks: "Thank you for your review!",
        waiting: "Thank you! The shop will check it before it shows.",
        order: "From order {{number}}",
    },
    wishlist: {
        title: "Saved products",
        save: "Save",
        saved: "Saved",
        empty: "Nothing saved yet.",
        signIn: "Sign in to save products",
    },
    landing: {
        orderTitle: "Order now",
        option: "Choose",
        quantity: "Quantity",
        unitPrice: "Price",
        codOnly: "Cash on delivery — pay when you get it.",
        deliveryNote: "The delivery charge for your area is added to the total. We will call you to confirm.",
        place: "Confirm order",
        placing: "Placing your order…",
        unavailable: "This product cannot be ordered right now.",
        countdown: {
            days: "Days",
            hours: "Hours",
            minutes: "Minutes",
            seconds: "Seconds",
            ended: "This offer has ended.",
        },
    },
    engine: {
        close: "Close",
        previous: "Previous",
        next: "Next",
        slide: "Slide {{n}} of {{total}}",
        carousel: "Products",
        decrease: "Decrease quantity",
        increase: "Increase quantity",
        quantity: "Quantity",
        rating: "{{value}} out of 5",
        reviewsCount: "({{n}})",
        compare: "Drag to compare before and after",
        before: "Before",
        after: "After",
        code: "Code from the SMS",
        digit: "Digit {{n}}",
        undo: "Undo",
        added: "Added to your cart",
        drawer: {
            emptyBody: "Browse the shop and add what you like — it waits here for you.",
            each: "{{price}} each",
            couponToggle: "Have a coupon code?",
            removed: "{{name}} removed",
            updating: "Updating",
            freeDeliveryMissing: "Add {{amount}} more for free delivery",
            freeDeliveryReached: "You get free delivery",
            cartLabel: "Cart, {{count}} items",
            /** ST-19: the automatic offer the cart is closest to (amount and offer filled in). */
            offerMissing: "Add {{amount}} more for {{offer}}",
            count_one: "{{n}} item",
            count_other: "{{n}} items",
        },
        // ST-19: automatic offers in words (cart line, product page note).
        offer: {
            percent: "{{percent}} off",
            amount: "{{amount}} off",
            cap: "up to {{amount}}",
            from: "{{offer}} on orders from {{min}}",
            always: "{{offer}} on every order",
            label: "Offer",
        },
        // ST-17 (single_product "Launch"): the pack choice, the live total and the calls to order.
        // ST-23 (home_food "Rannaghor"): the menu card and the delivery checker.
        // DG-08 (books "Boighor"): the search-first header, shelves and the footer line.
        boighor: {
            search: "Search books, authors, publishers or ISBN",
            subjects: "Subjects",
            authors: "Authors",
            ebooks: "eBooks",
            readHere: "Read on this website",
            seeAll: "See all",
            library: "My library",
            tagline: "Books for every reader — at your door, or to read right here.",
        },
        rannaghor: {
            checkDelivery: "Check delivery",
            menu: "Our menu",
            menuKicker: "Made to order",
        },
        // ST-22 (kids_baby "Tuntuni"): ages and the gift finder.
        tuntuni: {
            byAge: "Shop by age",
            giftFinder: "Gift finder",
        },
        // ST-21 (home_kitchen "Nest"): rooms, shop the room, inspiration, fragile items.
        nest: {
            shopTheRoom: "Shop the room",
            tapDots: "Tap a dot to see the piece",
            inspiration: "Inspiration",
            rooms: "Rooms",
            materials: "Materials & care",
            homes: "In your homes",
            fragile: "Fragile: packed in bubble wrap inside a hard box.",
        },
        // ST-20 (jewellery "Noor"): gift wish on the product page, occasions, the gift guide.
        gift: {
            gift: "This is a gift — wrap it, no price inside",
            message: "Message for the card (optional)",
            hint: "We add it to your order note at checkout.",
            note: "Gift: please wrap it, no price inside.",
            noteWith: "Gift: please wrap it, no price inside. Card message: {{message}}",
        },
        noor: {
            styleWith: "Style it with",
            giftGuide: "Gift guide",
            shop: "Shop",
            scroll: "Scroll",
        },
        // ST-19 (fashion_men "Shaan"): size boxes, combos and the fit band.
        shaan: {
            sizesOf: "Sizes of {{name}}",
            colours_one: "{{n}} colour",
            colours_other: "{{n}} colours",
            shopAll: "Shop all",
            comboKicker: "Buy together, pay less",
            fitKicker: "Size guide",
        },
        // ST-18 (cosmetics "Lavanya"): authenticity, the skin quiz and "suitable for".
        lavanya: {
            original: "100% original",
            promise: "Every product is original, from authorised importers, with its batch and expiry date shown.",
            quiz: "Skin quiz",
            step: "Question {{n}} of {{total}}",
            back: "Back",
            showing: "Showing your matches…",
            suitableFor: "Suitable for",
            shades_one: "{{n}} shade",
            shades_other: "{{n}} shades",
        },
        launch: {
            choose: "Choose {{option}}",
            bestValue: "Best value",
            stepOrder: "Your order",
            stepDelivery: "Where should we deliver?",
            summary: "Order summary",
            deliveryPending: "Choose your district",
            missing: "Still needed: {{fields}}",
            choice: "Your choice",
            phoneHint: "We call this number to confirm the order.",
            phoneInvalid: "Enter an 11-digit mobile number starting with 01.",
            nameMissing: "Please write your name.",
            addressMissing: "Please write your house, road and area.",
            namePlaceholder: "e.g. Rahima Akter",
            phonePlaceholder: "01XXXXXXXXX",
            addressPlaceholder: "House, road, area",
            addressHint: "So the courier can find you — a landmark helps.",
            checkFirst: "Check it in front of the delivery person before you pay.",
            weCall: "We call you to confirm before we send it.",
            orCall: "Prefer to order by phone?",
            subtotal: "Price",
            discount: "Offer",
            delivery: "Delivery",
            total: "Total",
            updating: "Updating the total…",
            noAdvance: "No advance payment — pay when it arrives.",
            placeTotal: "Confirm order · {{total}}",
            orderNow: "Order now",
            callToOrder: "Call to order",
            orderBar: "Order",
            yes: "Yes",
            no: "No",
        },
        compareList: {
            add: "Compare",
            added: "Comparing",
            full: "You can compare up to 3",
            tray: "Compare products",
            open: "Compare ({{n}})",
            clear: "Clear",
            remove: "Remove {{name}}",
            title: "Compare",
            intro: "Side by side, from each product's specifications.",
            empty: "Choose up to 3 products with “Compare” on their cards to see them side by side.",
            browse: "Browse products",
            price: "Price",
            availability: "Availability",
            view: "View",
            missing: "—",
            same: "Show only differences",
            all: "Show every row",
        },
        footer: { shop: "Shop", help: "Help", follow: "Follow", all: "All products", contact: "Contact" },
        chrome: {
            // ST-16: the grocery header and the phone cart bar.
            deliveringTo: "Delivering to {{place}}",
            deliveringToDays: "Delivering to {{place}} · {{days}}",
            deliveryEverywhere: "Delivery all over Bangladesh",
            searchGrocery: "Search honey, ghee, dates…",
            cartBar: "Your cart",
            cartBarItems_one: "{{n}} item",
            cartBarItems_other: "{{n}} items",
            skipToContent: "Skip to content",
            chatWhatsapp: "Chat on WhatsApp",
            chatMessenger: "Chat on Messenger",
            trust: "Why shop with us",
            menu: "Menu",
            openMenu: "Open the menu",
            search: "Search",
            searchTitle: "Search the shop",
            recent: "Recent searches",
            clear: "Clear",
            trending: "Popular searches",
            products: "Products",
            categories: "Categories",
            seeAll: "See all results for “{{q}}”",
            noResults: "Nothing matches “{{q}}”. Try another word or check the spelling.",
            searchFailed: "Search is not available right now. Please try again.",
            viewAll: "View all {{name}}",
            dismiss: "Hide this message",
            home: "Home",
            account: "Account",
            cartTab: "Cart",
            myOrders: "My orders",
            wishlist: "Wishlist",
        },
        card: {
            new: "New",
            colours: "Colours",
            quickAdd: "Add to cart",
            quickView: "Quick view",
            viewDetails: "See full details",
            save: "Save {{name}}",
            // ST-16 (grocery cards): "+ Add" that turns into a stepper, weight chips and the price per kilo.
            add: "Add",
            addItem: "Add {{name}} to cart",
            quantityOf: "Quantity of {{name}}",
            sizes: "Sizes of {{name}}",
            perKg: "{{price}}/kg",
            perL: "{{price}}/L",
        },
        list: {
            price: "Price (৳)",
            min: "Min",
            max: "Max",
            brand: "Brand",
            publisher: "Publisher",
            author: "Author",
            format: "Format",
            formats: { physical: "Hard copy", digital: "eBook" },
            showResults: "Show results",
            clearAll: "Clear all",
            subcategories: "Sub-categories",
            showing: "{{n}} products shown",
            active: "Chosen filters",
            remove: "Remove {{name}}",
            loading: "Loading…",
            grid: "Grid",
            dense: "Small grid",
            list: "List",
            view: "View",
        },
        product: {
            buyNow: "Buy now",
            zoom: "Zoom",
            sizeGuide: "Size guide",
            units: { inch: "Measurements in inches", cm: "Measurements in centimetres" },
            fitTitle: "Find your size",
            measurement: "Measure",
            yourMeasure: "Yours",
            suggestion: "Size {{size}} should fit you.",
            noSuggestion: "Larger than our biggest size — please call us.",
            howToMeasure: "How to measure",
            deliveryTo: "Delivery to",
            chooseDistrict: "Choose district",
            deliveryLine: "Delivery {{charge}} · {{days}}",
            deliveryLineNoDays: "Delivery {{charge}}",
            days: "{{min}}–{{max}} days",
            freeFrom: "Free delivery on orders from {{amount}}",
            share: "Share",
            whatsapp: "WhatsApp",
            facebook: "Facebook",
            copyLink: "Copy link",
            copied: "Link copied",
            specs: "Specifications",
            deliveryReturns: "Delivery & returns",
            readPolicies: "Read the full policy",
            pairs: "Pairs well with",
            bundle: "All together: {{price}}",
            related: "You may also like",
            recent: "Recently viewed",
        },
        order: {
            title: "Please fill in the form to order",
            searchDistrict: "Search district…",
            searchUpazila: "Search area…",
            address: "Full address (house, road, village)",
            required: "Please fill in the marked fields.",
            shipping: "Shipping",
            discount: "Discount",
            orderWith: "Order now — {{method}}",
            payWith: "Pay {{amount}} with {{method}}",
            steps: { order: "Order", check: "Check", confirm: "Confirm", done: "Done" },
            progress: {
                orderTitle: "Placing your order",
                orderBody: "Sending your details to the shop.",
                checkTitle: "Checking details",
                checkBody: "Checking phone number, address and stock.",
                confirmTitle: "Confirming your order",
                confirmBody: "Your order is in — preparing your receipt.",
                doneTitle: "All done",
                doneBody: "Your order is confirmed.",
                keepOpen: "Please don't refresh or close this page.",
            },
            success: {
                thanks: "Thank you, {{name}}!",
                confirmed: "Your order #{{number}} is confirmed.",
                next: "We will call or text you shortly to confirm delivery.",
                details: "Customer details",
                contact: "Contact",
                address: "Delivery address",
                track: "Track my order",
            },
        },
        placed: {
            call: "We call or text you to confirm the order and the delivery address.",
            pack: "We pack your order with care.",
            deliver: "The courier brings it to you — pay when it arrives (cash on delivery).",
        },
        gallery: {
            title: "Engine gallery",
            intro: "Every building block of the shop themes, styled by this shop's theme. Not shown in production.",
            sheet: "Drawer",
            dialog: "Dialog",
            openRight: "Open from the right",
            openLeft: "Open from the left",
            openBottom: "Open from the bottom",
            openDialog: "Open dialog",
            carousel: "Carousel",
            tabs: "Tabs",
            accordion: "Accordion",
            controls: "Choices, quantity, rating and price",
            countdown: "Countdown",
            toast: "Toast",
            showToast: "Show a message",
            motion: "Motion",
            marquee: "Free delivery over ৳2,000 · Cash on delivery · Easy exchange",
            hotspots: "Shop the look",
            beforeAfter: "Before and after",
            otp: "One-time code",
            fonts: "Theme fonts",
            fontSample: "New arrivals for Eid — handpicked and delivered to your door",
            bengali: "Bengali text",
            sample: "ক্ষমা, ন্ত্র, র‍্যাব, ব্যাগ, স্বপ্ন, শ্রী, জ্ঞান, কৃষ্ণ — সারা দেশে ক্যাশ অন ডেলিভারি।",
        },
    },
    sections: shopSections,
    policies: { returns: "Returns & refunds", delivery: "Delivery", privacy: "Privacy", terms: "Terms" },
    social: {
        facebook: "Facebook",
        instagram: "Instagram",
        youtube: "YouTube",
        tiktok: "TikTok",
        whatsapp: "WhatsApp",
    },
};
//# sourceMappingURL=shop.js.map